import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Eye, User, Mail, Phone, ShoppingBag, Euro, Edit } from 'lucide-react';
import axios from 'axios';
import { formatPrice } from '../../utils/priceFormatter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminClients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedClient, setEditedClient] = useState(null);
  const [stats, setStats] = useState({ total_clients: 0, total_orders: 0, total_revenue: 0 });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchClients();
    fetchStats();
  }, []);

  useEffect(() => {
    const filtered = clients.filter(client =>
      client.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.customer_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.customer_phone.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredClients(filtered);
  }, [searchQuery, clients]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/clients/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/clients/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewClient = async (client) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/clients/${encodeURIComponent(client.customer_email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedClient(response.data);
      setEditedClient({ ...response.data });
      setIsEditMode(false);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching client details:', error);
      alert('Erreur lors du chargement des détails du client');
    }
  };

  const handleEditClient = () => {
    setIsEditMode(true);
  };

  const handleSaveClient = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/clients/${encodeURIComponent(selectedClient.customer_email)}`,
        {
          customer_name: editedClient.customer_name,
          customer_email: editedClient.customer_email,
          customer_phone: editedClient.customer_phone
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchClients();
      setSelectedClient(editedClient);
      setIsEditMode(false);
      alert('Client mis à jour avec succès');
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Erreur lors de la mise à jour du client');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestion des clients</h1>
                <p className="text-sm text-gray-600">{filteredClients.length} clients</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Clients</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_clients}</p>
              </div>
              <User className="text-blue-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Commandes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_orders}</p>
              </div>
              <ShoppingBag className="text-green-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Revenu Total</p>
                <p className="text-2xl font-bold text-gray-900">{formatPrice(stats.total_revenue)} €</p>
              </div>
              <Euro className="text-purple-500" size={32} />
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher par nom, email ou téléphone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon"
            />
          </div>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commandes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total dépensé</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière commande</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredClients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-celadon rounded-full flex items-center justify-center text-white font-bold">
                        {client.customer_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="font-medium text-gray-900">{client.customer_name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="flex items-center gap-1 text-gray-900">
                        <Mail size={14} />
                        {client.customer_email}
                      </div>
                      <div className="flex items-center gap-1 text-gray-500 mt-1">
                        <Phone size={14} />
                        {client.customer_phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-900 font-medium">{client.total_orders}</td>
                  <td className="px-6 py-4 text-gray-900 font-medium">{formatPrice(client.total_spent)} €</td>
                  <td className="px-6 py-4 text-gray-900">{formatDate(client.last_order_date)}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleViewClient(client)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Voir les détails"
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Client Details Modal */}
      {showModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">Détails du client</h2>
                <p className="text-gray-600 mt-1">{selectedClient.customer_email}</p>
              </div>
              {!isEditMode && (
                <button
                  onClick={handleEditClient}
                  className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                >
                  <Edit size={18} />
                  Modifier
                </button>
              )}
            </div>
            
            <div className="p-6 space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium mb-3">Informations personnelles</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500">Nom:</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={editedClient.customer_name}
                          onChange={(e) => setEditedClient({ ...editedClient, customer_name: e.target.value })}
                          className="w-full mt-1 px-2 py-1 border rounded focus:outline-none focus:border-celadon"
                        />
                      ) : (
                        <p className="font-medium">{selectedClient.customer_name}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Email:</label>
                      {isEditMode ? (
                        <input
                          type="email"
                          value={editedClient.customer_email}
                          onChange={(e) => setEditedClient({ ...editedClient, customer_email: e.target.value })}
                          className="w-full mt-1 px-2 py-1 border rounded focus:outline-none focus:border-celadon"
                        />
                      ) : (
                        <p className="font-medium">{selectedClient.customer_email}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Téléphone:</label>
                      {isEditMode ? (
                        <input
                          type="tel"
                          value={editedClient.customer_phone}
                          onChange={(e) => setEditedClient({ ...editedClient, customer_phone: e.target.value })}
                          className="w-full mt-1 px-2 py-1 border rounded focus:outline-none focus:border-celadon"
                        />
                      ) : (
                        <p className="font-medium">{selectedClient.customer_phone}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium mb-3">Statistiques</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total commandes:</span>
                      <span className="font-medium">{selectedClient.total_orders}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total dépensé:</span>
                      <span className="font-medium">{formatPrice(selectedClient.total_spent)} €</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Première commande:</span>
                      <span className="font-medium">{formatDate(selectedClient.first_order_date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dernière commande:</span>
                      <span className="font-medium">{formatDate(selectedClient.last_order_date)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              {selectedClient.delivery_address && (
                <div>
                  <h3 className="font-medium mb-2">Dernière adresse de livraison</h3>
                  <div className="text-sm bg-gray-50 p-3 rounded">
                    <p>{selectedClient.delivery_address.street}</p>
                    <p>{selectedClient.delivery_address.postal_code} {selectedClient.delivery_address.city}</p>
                    <p>{selectedClient.delivery_address.country}</p>
                  </div>
                </div>
              )}

              {/* Orders History */}
              <div>
                <h3 className="font-medium mb-3">Historique des commandes</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedClient.orders && selectedClient.orders.map((order) => (
                    <div key={order.id} className="border rounded p-3 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{order.order_number}</p>
                          <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                          <p className="text-xs text-gray-600 mt-1">{order.items.length} article(s)</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{formatPrice(order.total)} €</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            order.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                            order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex gap-3">
              {isEditMode ? (
                <>
                  <button
                    onClick={handleSaveClient}
                    className="flex-1 px-4 py-2 bg-celadon text-white hover:bg-celadon-light rounded transition-colors"
                  >
                    Enregistrer
                  </button>
                  <button
                    onClick={() => {
                      setIsEditMode(false);
                      setEditedClient({ ...selectedClient });
                    }}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                  >
                    Annuler
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                >
                  Fermer
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClients;
